/**
 * The seven section icons.
 *
 * Hand-drawn inline SVG rather than an icon package: `DESIGN-SYSTEM.md` rule 2
 * — primitives are built against the tokens because the client owns the IP and
 * would otherwise inherit a dependency's conventions (and its licence) for
 * years. Seven 18px line glyphs do not justify one.
 *
 * All of them: 18×18, `currentColor`, 1.5 stroke, and `aria-hidden` — an icon
 * never carries the name. The rail supplies that as a real label and a tooltip,
 * because **an icon alone is not a label**: on a 64px rail with no text, a
 * reader who does not recognise the glyph has no way back to the word.
 */
type IconProps = { className?: string }

function Svg({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {children}
    </svg>
  )
}

/** Stories — a phone frame, which is what a Snap story is. */
export const StoriesIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="7" y="2.5" width="10" height="19" rx="2.5" />
    <path d="M10.5 5.5h3" />
  </Svg>
)

/** Spotlights — a play triangle. */
export const SpotlightsIcon = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M10 8.5l6 3.5-6 3.5z" />
  </Svg>
)

/** Channel Overview — the line chart the page is mostly made of. */
export const ChannelsIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M3 20h18" />
    <path d="M4 15l5-5 4 3 6-7" />
  </Svg>
)

/** Revenue — a banknote. */
export const RevenueIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="2.5" y="6" width="19" height="12" rx="2" />
    <circle cx="12" cy="12" r="2.5" />
  </Svg>
)

/** Raw data — a table. */
export const RawDataIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M3 9.5h18M9 9.5V20" />
  </Svg>
)

/** Connections — pipeline health, drawn as linked nodes. */
export const ConnectionsIcon = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="6" cy="6" r="2.5" />
    <circle cx="18" cy="18" r="2.5" />
    <path d="M6 8.5v5a4 4 0 004 4h5.5" />
  </Svg>
)

/** Users. */
export const UsersIcon = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="9" cy="8" r="3.25" />
    <path d="M3.5 19.5a5.5 5.5 0 0111 0" />
    <path d="M16 5.6a3.25 3.25 0 010 4.8M17.5 14.6a5.5 5.5 0 013 4.9" />
  </Svg>
)

/**
 * Legacy Tableau — a frozen pane.
 *
 * A dashboard grid with a *snowflake* over it, because the one thing a reader
 * must know before opening these is that the data stopped on 10 July 2026 and
 * never moves again. A plain chart glyph would say "dashboards", which is the
 * half that is not worth an icon.
 */
export const LegacyIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="2.5" y="3.5" width="19" height="17" rx="2" />
    <path d="M2.5 9h19" />
    <path d="M9 9v11.5" />
    <path d="M16 12.5v4M14 13.5l4 2M18 13.5l-4 2" />
  </Svg>
)

export const SECTION_ICONS: Record<string, (props: IconProps) => React.JSX.Element> = {
  stories: StoriesIcon,
  spotlights: SpotlightsIcon,
  channels: ChannelsIcon,
  revenue: RevenueIcon,
  'raw-data': RawDataIcon,
  connections: ConnectionsIcon,
  legacy: LegacyIcon,
  users: UsersIcon,
}
